import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Button,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fade,
  Zoom,
  Card,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from "@mui/material";
import { styled, alpha, keyframes } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaidIcon from "@mui/icons-material/Paid";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CloseIcon from "@mui/icons-material/Close";
import { REACT_APP_BEFORE_LOGIN_REDIRECT_URL } from "../../config";
import Header from "../../components/Header";
import useApp from "../../store/contexts/AppContext";
import capitalizeEachFirstWord from "../../utils/CapLetters";
import formatDateDurationInDaysAndHours from "../../utils/formatDateDurationInDaysAndHours";
import AdBanner from "../../components/Ads/AdBanner";
import AdWidget from "../../components/Ads/AdWidget";
import BookmarkButton from "../../components/Bookmarks/BookmarkButton";
import TrendingRooms from "../../components/Others/TrendingRooms";
import { ROUTES } from "../../routes/User/paths";

// Keyframe animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#0a0a0a" : "#f8f9fa",
  minHeight: "100vh",
  color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.text.primary,
  paddingBottom: theme.spacing(10),
  position: "relative",
  transition: "background-color 0.3s ease, color 0.3s ease",
  "&::before": {
    content: '""',
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "400px",
    background: `radial-gradient(ellipse at top, ${alpha(
      theme.palette.primary.main,
      theme.palette.mode === "dark" ? 0.2 : 0.15,
    )}, transparent)`,
    pointerEvents: "none",
    zIndex: 0,
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  width: "92%",
  maxWidth: 1400,
  margin: "30px auto 0px",
  paddingBottom: "80px",
  position: "relative",
  zIndex: 1,
}));

const MainContentArea = styled(Box)(({ theme }) => ({
  animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1)`,
}));

const SidebarArea = styled(Box)(({ theme }) => ({
  animation: `${slideInRight} 0.8s cubic-bezier(0.4, 0, 0.2, 1)`,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const HeroSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(5),
  padding: theme.spacing(4, 0),
  animation: `${fadeInUp} 0.7s cubic-bezier(0.4, 0, 0.2, 1)`,
}));

const WelcomeCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 24,
  background: theme.palette.mode === "dark"
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.dark, 0.05)})`
    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.light, 0.03)})`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.15)}`,
  boxShadow: theme.palette.mode === "dark"
    ? `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`
    : `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  marginBottom: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "40%",
    height: "100%",
    background: `radial-gradient(circle at top right, ${alpha(
      theme.palette.primary.main,
      theme.palette.mode === "dark" ? 0.1 : 0.05,
    )}, transparent)`,
    pointerEvents: "none",
  },
}));

const WelcomeTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 900,
  background: theme.palette.mode === "dark"
    ? `linear-gradient(135deg, ${theme.palette.common.white}, ${alpha(theme.palette.common.white, 0.7)})`
    : `linear-gradient(135deg, ${theme.palette.text.primary}, ${alpha(theme.palette.text.primary, 0.7)})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: theme.spacing(1),
  letterSpacing: "-0.02em",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  transition: "all 0.3s ease",
}));

const WelcomeSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.7)
    : alpha(theme.palette.text.primary, 0.7),
  marginBottom: theme.spacing(3),
  lineHeight: 1.6,
  transition: "color 0.3s ease",
}));

const StatsRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.primary.main, 0.03),
  backdropFilter: "blur(10px)",
  border: `1px solid ${theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.primary.main, 0.15)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.primary.main, 0.08),
    transform: "translateY(-4px)",
    boxShadow: theme.palette.mode === "dark"
      ? `0 8px 24px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 800,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(0.5),
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.6)
    : alpha(theme.palette.text.primary, 0.6),
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  transition: "color 0.3s ease",
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.03)
      : alpha(theme.palette.primary.main, 0.05),
    color: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.9)
      : theme.palette.text.primary,
    fontWeight: 700,
    fontSize: "0.85rem",
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    padding: theme.spacing(2, 2.5),
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    position: "relative",
    transition: "all 0.3s ease",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "2px",
      background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
      opacity: 0,
      transition: "opacity 0.3s ease",
    },
    "&:hover::after": {
      opacity: 1,
    },
  },
}));

const TableBodyCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.85)
    : alpha(theme.palette.text.primary, 0.9),
  fontSize: "0.9rem",
  borderBottom: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.05)}`
    : `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  padding: theme.spacing(2, 2.5),
  transition: "all 0.3s ease",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.02)
    : "transparent",
  position: "relative",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: "translateX(6px)",
    boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`,
    "& td": {
      color: theme.palette.mode === "dark"
        ? theme.palette.common.white
        : theme.palette.text.primary,
    },
  },
  "&:last-child td": {
    borderBottom: "none",
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  animation: `${fadeInUp} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down("sm")]: {
    flex: "0 0 90%",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 14,
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.03),
    backdropFilter: "blur(10px)",
    border: theme.palette.mode === "dark"
      ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
      : `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.07)
        : alpha(theme.palette.common.black, 0.05),
      border: theme.palette.mode === "dark"
        ? `1px solid ${alpha(theme.palette.common.white, 0.15)}`
        : `1px solid ${alpha(theme.palette.common.black, 0.18)}`,
    },
    "& fieldset": {
      borderColor: "transparent",
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
      "& fieldset": {
        borderColor: "transparent",
      },
    },
  },
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.75, 2),
    color: theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.text.primary,
    fontSize: "0.95rem",
  },
}));

const FilterSelect = styled(Select)(({ theme }) => ({
  minWidth: 180,
  borderRadius: 14,
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.common.black, 0.03),
  backdropFilter: "blur(10px)",
  color: theme.palette.mode === "dark"
    ? theme.palette.common.white
    : theme.palette.text.primary,
  border: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
    : `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.07)
      : alpha(theme.palette.common.black, 0.05),
    border: theme.palette.mode === "dark"
      ? `1px solid ${alpha(theme.palette.common.white, 0.15)}`
      : `1px solid ${alpha(theme.palette.common.black, 0.18)}`,
  },
  "&.Mui-focused": {
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.primary.main, 0.05),
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
  },
  "& .MuiSvgIcon-root": {
    color: theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.text.primary,
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 20,
  overflowX: "auto",
  overflowY: "hidden",
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.02)
    : alpha(theme.palette.common.white, 0.6),
  backdropFilter: "blur(20px)",
  border: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
    : `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  marginBottom: theme.spacing(4),
  boxShadow: theme.palette.mode === "dark"
    ? `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`
    : `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  animation: `${fadeInUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both`,
  // Smooth scrollbar styling
  "&::-webkit-scrollbar": {
    height: 8,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.05),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.2)
      : alpha(theme.palette.primary.main, 0.3),
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.3)
        : alpha(theme.palette.primary.main, 0.5),
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  fontSize: "0.75rem",
  fontWeight: 600,
  padding: theme.spacing(0.5, 1.25),
  minWidth: 50,
  textTransform: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: 0,
    height: 0,
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.15)",
    transform: "translate(-50%, -50%)",
    transition: "width 0.6s, height 0.6s",
  },
  "&:hover::before": {
    width: "300px",
    height: "300px",
  },
}));

const JoinButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "translateY(-3px)",
    boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.6)}`,
  },
  "&:active": {
    transform: "translateY(-1px)",
  },
}));

const SpectateButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: "transparent",
  border: `2px solid ${theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.2)
      : alpha(theme.palette.primary.main, 0.3)
    }`,
  color:
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.primary.main,
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.primary.main, 0.1),
    borderColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.4)
        : theme.palette.primary.main,
    transform: "translateY(-3px)",
  },
}));

const DeleteButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: "transparent",
  border: `2px solid ${alpha(theme.palette.error.main, 0.2)}`,
  color: theme.palette.error.main,
  "&:hover": {
    backgroundColor: alpha(theme.palette.error.main, 0.08),
    borderColor: alpha(theme.palette.error.main, 0.4),
    transform: "translateY(-3px)",
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: theme.spacing(4),
  animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both`,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

const PageSizeContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.common.black, 0.03),
  padding: theme.spacing(1.25, 2),
  borderRadius: 14,
  border: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
    : `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
}));

const PaginationControls = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const PaginationButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark"
    ? theme.palette.common.white
    : theme.palette.text.primary,
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.common.black, 0.03),
  border: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
    : `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    border: `1px solid ${theme.palette.primary.main}`,
    transform: "scale(1.15)",
  },
  "&.Mui-disabled": {
    color: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.2)
      : alpha(theme.palette.text.primary, 0.3),
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.02)
      : alpha(theme.palette.common.black, 0.01),
  },
}));

const PageIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 45,
  height: 45,
  borderRadius: 14,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.common.white,
  fontWeight: 700,
  fontSize: "1rem",
  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
  animation: `${pulse} 2s ease-in-out infinite`,
}));

const FloatingActionButton = styled(Button)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  borderRadius: "50%",
  width: 68,
  height: 68,
  minWidth: "auto",
  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
  color: theme.palette.common.white,
  boxShadow: `0 8px 28px ${alpha(theme.palette.secondary.main, 0.5)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  zIndex: 1000,
  animation: `${float} 3s ease-in-out infinite`,
  "&:hover": {
    transform: "translateY(-6px) scale(1.1)",
    boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.7)}`,
  },
  "&:active": {
    transform: "translateY(-3px) scale(1.05)",
  },
  [theme.breakpoints.down("sm")]: {
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    width: 60,
    height: 60,
  },
}));

const UsersChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.15),
  color: theme.palette.primary.light,
  fontWeight: 600,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  transition: "all 0.3s ease",
  "& .MuiChip-icon": {
    color: theme.palette.primary.light,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    transform: "scale(1.08)",
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8),
  textAlign: "center",
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.5)
    : alpha(theme.palette.text.primary, 0.6),
}));

export default function EnhancedDashboard() {
  const [searchType, setSearchType] = useState("topic");
  const [searchTerm, setSearchTerm] = useState("");
  const [toggleOnRoomCreationModal, setToggleOnRoomCreationModal] =
    useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deletedRoomIds, setDeletedRoomIds] = useState([]);
  const [showHero, setShowHero] = useState(true);
  const [mobileFilterModalOpen, setMobileFilterModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomDetailsOpen, setRoomDetailsOpen] = useState(false);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    appState: { groupChatLoading, allGroupChats, accessToken, userData },
  } = useApp();

  useEffect(() => {
    if (!accessToken) {
      history.push(REACT_APP_BEFORE_LOGIN_REDIRECT_URL);
    }
  }, [accessToken, history]);

  const handleChangePage = (newPage) => {
    if (newPage > 0 && newPage <= (allGroupChats?.total_pages || 1)) {
      setPage(newPage);
    }
  };

  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  const roomFilterOptions = [
    { value: "topic", label: "Room Topic" },
    { value: "host", label: "Host Name" },
    { value: "users", label: "Number of Users" },
    { value: "age", label: "Room Age" },
    { value: "cost", label: "Cost" },
    { value: "tags", label: "Room Tags" },
  ];

  const rows =
    allGroupChats?.data?.map((item) => ({
      id: item.id,
      topic: item.name,
      host: capitalizeEachFirstWord(item.admin_username),
      adminUsername: item.admin_username,
      users: item.total_participants,
      age: formatDateDurationInDaysAndHours(item.created_at),
      cost: item.join_fee,
      timeLeft: formatDateDurationInDaysAndHours(item.expiration_time),
      createdAt: item.created_at,
      description: item.description || item.room_description || "",
      tags: item.tags,
    })) || [];

  const xxx = allGroupChats?.data?.map((item) => {
    console.log("Mapping room item:", item);
    return item;
  });

  const filteredRows = rows.filter((row) => {
    console.log("Filtering row:", row);
    if (deletedRoomIds.includes(row.id)) return false;
    if (!searchTerm) return true;
    const value = row[searchType]?.toString().toLowerCase();
    return value?.includes(searchTerm.toLowerCase());
  });

  const totalPages = allGroupChats?.total_pages || 1;
  const currentPage = allGroupChats?.current_page || 1;
  const currentFilterLabel =
    roomFilterOptions.find((option) => option.value === searchType)?.label ||
    "Room Topic";
  const selectedRoomTags = Array.isArray(selectedRoom?.tags)
    ? selectedRoom.tags
    : selectedRoom?.tags
      ? String(selectedRoom.tags)
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
      : [];

  const handleOpenRoomDetails = (room) => {
    setSelectedRoom(room);
    setRoomDetailsOpen(true);
  };

  const handleCloseRoomDetails = () => {
    setRoomDetailsOpen(false);
    setSelectedRoom(null);
  };

  const handleRoomJoin = (roomId) => {
    history.push(`${ROUTES.rooms}/${roomId}`);
  };

  const handleJoinFromModal = () => {
    if (!selectedRoom?.id) return;
    handleCloseRoomDetails();
    handleRoomJoin(selectedRoom.id);
  };

  const handleRoomSpectate = (roomId) => {
    history.push(`${ROUTES.spectate}/${roomId}`);
  };

  const handleSpectateFromModal = () => {
    if (!selectedRoom?.id) return;
    handleCloseRoomDetails();
    handleRoomSpectate(selectedRoom.id);
  };

  const handleMobileFilterSelect = (filterValue) => {
    setSearchType(filterValue);
    setMobileFilterModalOpen(false);
  };

  const handleRoomDelete = (roomId) => {
    const confirmed = window.confirm("Are you sure you want to delete this room? This action cannot be undone.");
    if (confirmed) {
      setDeletedRoomIds((prev) => [...prev, roomId]);
    }
  };

  // Calculate stats
  const totalRooms = allGroupChats?.total || 0;
  const activeRooms = rows.length;

  return (
    <DashboardContainer>
      <Header
        toggleOnRoomCreationModal={toggleOnRoomCreationModal}
        setToggleOnRoomCreationModal={setToggleOnRoomCreationModal}
      />

      <ContentContainer>
        {/* Hero Welcome Section */}
        {showHero && (
          <HeroSection sx={{ position: "relative" }}>
            <Tooltip title="Dismiss" arrow>
              <IconButton
                onClick={() => setShowHero(false)}
                size="small"
                sx={{
                  position: "absolute",
                  top: 50,
                  right: 12,
                  zIndex: 1,
                  color: alpha("#fff", 0.6),
                  "&:hover": { color: "#fff", backgroundColor: alpha("#fff", 0.1) },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <WelcomeCard elevation={0}>
              <WelcomeTitle>
                <RocketLaunchIcon
                  sx={{ fontSize: "3rem", color: "primary.main" }}
                />
                Welcome back,{" "}
                {capitalizeEachFirstWord(userData?.username || "User")}!
              </WelcomeTitle>
              <WelcomeSubtitle>
                Discover amazing conversations and connect with people who share
                your interests
              </WelcomeSubtitle>

              <StatsRow>
                <StatCard>
                  <StatValue>{totalRooms}</StatValue>
                  <StatLabel>Total Rooms</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{activeRooms}</StatValue>
                  <StatLabel>Active Now</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>
                    {userData?.available_coins?.toLocaleString() || 0}
                  </StatValue>
                  <StatLabel>Your Coins</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>
                    {capitalizeEachFirstWord(userData?.account_type || "Basic")}
                  </StatValue>
                  <StatLabel>Account Tier</StatLabel>
                </StatCard>
              </StatsRow>
            </WelcomeCard>
          </HeroSection>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 4,
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {/* Main content area */}
          <MainContentArea sx={{ flex: 1 }}>
            {/* Hero Ad Banner - Only for Basic users */}
            {userData?.account_type?.toLowerCase() === "basic" && (
              <Zoom in timeout={600}>
                <Box sx={{ mb: 4 }}>
                  <AdBanner
                    position="hero"
                    maxAds={1}
                    autoRotate={true}
                    interval={10000}
                  />
                </Box>
              </Zoom>
            )}

            <SearchContainer id="room-search-controls">
              <SearchField
                id="room-search-input"
                placeholder="Search for rooms..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color: theme.palette.mode === "dark"
                            ? alpha(theme.palette.common.white, 0.5)
                            : alpha(theme.palette.text.primary, 0.5)
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {isMobile ? (
                <IconButton
                  id="room-search-filter-button"
                  aria-label="Open room filter options"
                  onClick={() => setMobileFilterModalOpen(true)}
                  sx={{
                    flex: "0 0 calc(10% - 4px)",
                    minWidth: 44,
                    width: 44,
                    height: 56,
                    borderRadius: 14,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.05)
                        : alpha(theme.palette.common.black, 0.03),
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                  }}
                >
                  <FilterListIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <FilterListIcon
                    sx={{
                      color: theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.5)
                        : alpha(theme.palette.text.primary, 0.5)
                    }}
                  />
                  <FilterSelect
                    id="room-search-filter"
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    displayEmpty
                  >
                    {roomFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </FilterSelect>
                </Box>
              )}
            </SearchContainer>

            <StyledTableContainer
              id="available-room-list"
              component={Paper}
              elevation={0}
            >
              <Table stickyHeader id="available-room-table">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {/* <TrendingUpIcon fontSize="small" /> */}
                        Topic
                      </Box>
                    </TableHeaderCell>
                    <TableHeaderCell align="center">Host</TableHeaderCell>
                    <TableHeaderCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <GroupIcon fontSize="small" />
                        Users
                      </Box>
                    </TableHeaderCell>
                    {!isMobile && (
                      <TableHeaderCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <CalendarMonthIcon fontSize="small" />
                          Age
                        </Box>
                      </TableHeaderCell>
                    )}
                    <TableHeaderCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <PaidIcon fontSize="small" />
                        Cost
                      </Box>
                    </TableHeaderCell>
                    {!isMobile && (
                      <TableHeaderCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <AccessTimeIcon fontSize="small" />
                          Expires
                        </Box>
                      </TableHeaderCell>
                    )}
                    {!isMobile && (
                      <TableHeaderCell align="center">Actions</TableHeaderCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length > 0 ? (
                    filteredRows.map((row, index) => (
                      <Fade
                        in
                        timeout={300}
                        style={{ transitionDelay: `${index * 50}ms` }}
                        key={row.id}
                      >
                        <StyledTableRow
                          id={`available-room-row-${row.id}`}
                          onClick={() => handleOpenRoomDetails(row)}
                        >
                          <TableBodyCell id={`room-topic-${row.id}`}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ letterSpacing: "0.01em" }}
                            >
                              {row.topic}
                            </Typography>
                          </TableBodyCell>
                          <TableBodyCell align="center" id={`room-host-${row.id}`}>
                            <a href={`/${row.host}`}>{row.host}</a>
                          </TableBodyCell>
                          <TableBodyCell align="center" id={`room-users-${row.id}`}>
                            <UsersChip
                              icon={<GroupIcon />}
                              label={row.users}
                              size="small"
                            />
                          </TableBodyCell>
                          {!isMobile && (
                            <TableBodyCell align="center" id={`room-age-${row.id}`}>
                              {row.age}
                            </TableBodyCell>
                          )}
                          <TableBodyCell align="center" id={`room-cost-${row.id}`}>
                            <Chip
                              label={
                                row.cost > 0 ? `${row.cost} coins` : "Free"
                              }
                              size="small"
                              sx={{
                                backgroundColor:
                                  row.cost > 0
                                    ? alpha(theme.palette.warning.main, 0.15)
                                    : alpha(theme.palette.success.main, 0.15),
                                color:
                                  row.cost > 0
                                    ? theme.palette.warning.light
                                    : theme.palette.success.light,
                                fontWeight: 600,
                                border: `1px solid ${alpha(
                                  row.cost > 0
                                    ? theme.palette.warning.main
                                    : theme.palette.success.main,
                                  0.3,
                                )}`,
                              }}
                            />
                          </TableBodyCell>
                          {!isMobile && (
                            <>
                              <TableBodyCell align="center" id={`room-time-${row.id}`}>
                                {row.timeLeft}
                              </TableBodyCell>
                              <TableBodyCell align="center">
                                <Box
                                  id={`room-actions-${row.id}`}
                                  onClick={(event) => event.stopPropagation()}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 0.5,
                                    justifyContent: "center",
                                  }}
                                >
                                  <Box id={`bookmark-room-button-${row.id}`}>
                                    <BookmarkButton
                                      roomId={row.id}
                                      size="small"
                                      color="tomato"
                                    />
                                  </Box>
                                  <JoinButton
                                    id={`join-room-button-${row.id}`}
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleRoomJoin(row.id)}
                                  >
                                    Join
                                  </JoinButton>
                                  <Tooltip title="Spectate Room" arrow>
                                    <SpectateButton
                                      id={`spectate-room-button-${row.id}`}
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleRoomSpectate(row.id)}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </SpectateButton>
                                  </Tooltip>
                                  {/* delete room - only visible to the room host */}
                                  {userData?.username?.toLowerCase() === row.host?.toLowerCase() && (
                                    <DeleteButton
                                      id={`delete-room-button-${row.id}`}
                                      roomId={row.id}
                                      size="small"
                                      onClick={() => handleRoomDelete(row.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </DeleteButton>
                                  )}
                                </Box>
                              </TableBodyCell>
                            </>
                          )}
                        </StyledTableRow>
                      </Fade>
                    ))
                  ) : (
                    <TableRow>
                      <TableBodyCell colSpan={isMobile ? 4 : 7}>
                        <EmptyState>
                          {searchTerm ? (
                            <>
                              <Typography variant="h6" sx={{ mb: 1 }}>
                                No results found for "{searchTerm}"
                              </Typography>
                              <Typography variant="body2">
                                Try a different search term or filter
                              </Typography>
                            </>
                          ) : groupChatLoading ? (
                            <CircularProgress size={32} />
                          ) : (
                            <>
                              <Typography variant="h6" sx={{ mb: 1 }}>
                                No rooms available
                              </Typography>
                              <Typography variant="body2">
                                Create a new room to get started
                              </Typography>
                            </>
                          )}
                        </EmptyState>
                      </TableBodyCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>

            {!groupChatLoading && filteredRows.length > 0 && (
              <PaginationContainer>
                <PageSizeContainer>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.7)
                        : alpha(theme.palette.text.primary, 0.7),
                    }}
                  >
                    Rows per page:
                  </Typography>
                  <FilterSelect
                    value={pageSize}
                    onChange={handleChangePageSize}
                    sx={{ minWidth: 70 }}
                  >
                    {[5, 10, 25, 50].map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </FilterSelect>
                </PageSizeContainer>

                <PaginationControls>
                  <PaginationButton
                    disabled={currentPage <= 1}
                    onClick={() => handleChangePage(currentPage - 1)}
                  >
                    <ChevronLeftIcon />
                  </PaginationButton>

                  {!isMobile && currentPage > 2 && (
                    <Typography
                      variant="body2"
                      sx={{ color: alpha(theme.palette.common.white, 0.3) }}
                    >
                      ...
                    </Typography>
                  )}

                  {currentPage > 1 && !isMobile && (
                    <Button
                      onClick={() => handleChangePage(currentPage - 1)}
                      sx={{
                        minWidth: 40,
                        height: 40,
                        borderRadius: "50%",
                        color: alpha(theme.palette.common.white, 0.7),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.common.white,
                            0.08,
                          ),
                        },
                      }}
                    >
                      {currentPage - 1}
                    </Button>
                  )}

                  <PageIndicator>{currentPage}</PageIndicator>

                  {currentPage < totalPages && !isMobile && (
                    <Button
                      onClick={() => handleChangePage(currentPage + 1)}
                      sx={{
                        minWidth: 40,
                        height: 40,
                        borderRadius: "50%",
                        color: alpha(theme.palette.common.white, 0.7),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.common.white,
                            0.08,
                          ),
                        },
                      }}
                    >
                      {currentPage + 1}
                    </Button>
                  )}

                  {!isMobile && currentPage < totalPages - 1 && (
                    <Typography
                      variant="body2"
                      sx={{ color: alpha(theme.palette.common.white, 0.3) }}
                    >
                      ...
                    </Typography>
                  )}

                  <PaginationButton
                    disabled={currentPage >= totalPages}
                    onClick={() => handleChangePage(currentPage + 1)}
                  >
                    <ChevronRightIcon />
                  </PaginationButton>
                </PaginationControls>

                {isMobile && (
                  <Typography
                    variant="body2"
                    sx={{ color: alpha(theme.palette.common.white, 0.7) }}
                  >
                    Page {currentPage} of {totalPages}
                  </Typography>
                )}
              </PaginationContainer>
            )}
          </MainContentArea>

          {/* Sidebar content */}
          <SidebarArea
            sx={{
              width: { xs: "100%", lg: "340px" },
              flexShrink: 0,
            }}
          >
            {/* Sidebar Ad Widget - Only for Basic users */}
            {userData?.account_type?.toLowerCase() === "basic" && (
              <Zoom in timeout={800}>
                <Box>
                  <AdWidget position="sidebar" maxAds={2} size="medium" />
                </Box>
              </Zoom>
            )}

            <Zoom in timeout={1000}>
              <Box>
                <TrendingRooms />
              </Box>
            </Zoom>
          </SidebarArea>
        </Box>
      </ContentContainer>

      <Zoom in timeout={1200}>
        <FloatingActionButton
          id="create-room-floating-button"
          variant="contained"
          onClick={() => setToggleOnRoomCreationModal(true)}
          aria-label="Create new room"
        >
          <AddIcon sx={{ fontSize: 32 }} />
        </FloatingActionButton>
      </Zoom>

      <Dialog
        open={mobileFilterModalOpen}
        onClose={() => setMobileFilterModalOpen(false)}
        fullWidth
        maxWidth="xs"
        id="room-filter-modal"
        aria-labelledby="room-filter-modal-title"
      >
        <DialogTitle id="room-filter-modal-title">
          Filter available rooms by
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1 }}>
            {roomFilterOptions.map((option) => (
              <Button
                key={option.value}
                id={`room-filter-option-${option.value}`}
                variant={searchType === option.value ? "contained" : "outlined"}
                onClick={() => handleMobileFilterSelect(option.value)}
                sx={{ justifyContent: "flex-start", textTransform: "none" }}
              >
                {option.label}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            id="room-filter-modal-close-button"
            onClick={() => setMobileFilterModalOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={roomDetailsOpen}
        onClose={handleCloseRoomDetails}
        fullWidth
        maxWidth="sm"
        id="room-details-modal"
        aria-labelledby="room-details-modal-title"
      >
        <DialogTitle
          id="room-details-modal-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              {selectedRoom?.host?.charAt(0)?.toUpperCase() || "R"}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {selectedRoom?.topic || "Room details"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hosted by {selectedRoom?.host || "Unknown host"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            id="room-details-modal-close-icon"
            onClick={handleCloseRoomDetails}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers id="room-details-modal-content">
          <Box sx={{ display: "grid", gap: 2.5 }}>
            {selectedRoom?.description && (
              <Typography id="room-detail-description" variant="body2">
                {selectedRoom.description}
              </Typography>
            )}

            <Divider />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box id="room-detail-id">
                <Typography variant="caption" color="text.secondary">
                  Room ID
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedRoom?.id || "N/A"}
                </Typography>
              </Box>

              <Box id="room-detail-host-name">
                <Typography variant="caption" color="text.secondary">
                  Host
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedRoom?.host || "Unknown"}
                </Typography>
              </Box>

              <Box id="room-detail-users">
                <Typography variant="caption" color="text.secondary">
                  Participants
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedRoom?.users ?? 0}
                </Typography>
              </Box>

              <Box id="room-detail-cost">
                <Typography variant="caption" color="text.secondary">
                  Join Cost
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedRoom?.cost > 0 ? `${selectedRoom.cost} coins` : "Free"}
                </Typography>
              </Box>

              <Box id="room-detail-age">
                <Typography variant="caption" color="text.secondary">
                  Room Age
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedRoom?.age || "N/A"}
                </Typography>
              </Box>

              <Box id="room-detail-created-at">
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedRoom?.createdAt
                    ? new Date(selectedRoom.createdAt).toLocaleString()
                    : "N/A"}
                </Typography>
              </Box>
            </Box>

            <Box id="room-detail-tags">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Tags
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedRoomTags.length > 0 ? (
                  selectedRoomTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                  ))
                ) : (
                  <Typography variant="body2">No tags available</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button id="room-details-modal-close-button" onClick={handleCloseRoomDetails}>
            Close
          </Button>
          <SpectateButton
            id="room-details-modal-spectate-button"
            variant="outlined"
            onClick={handleSpectateFromModal}
          >
            Spectate
          </SpectateButton>
          <JoinButton
            id="room-details-modal-join-button"
            variant="contained"
            onClick={handleJoinFromModal}
          >
            Join Room
          </JoinButton>
        </DialogActions>
      </Dialog>
    </DashboardContainer>
  );
}
